/**
 * Types for the derived filter/metric glossary (`GET /explorer/api/glossary`).
 *
 * Almost everything the backend sends is derived from code, vendor documentation
 * and the usage audit — see nba_explorer/lib/glossary.py. The authored layer is
 * an entry's `note` and the guides (nba_explorer/docs/glossary/*.md), topics that
 * span several entries.
 */
import { FilterStatus } from "./Explorer";

export interface GlossaryStats {
  /** Distinct queries that ever referenced this name. */
  queries?: number;
  /** Of those, ones deliberately saved as public or private. */
  savedQueries?: number;
  runs?: number;
  users?: number;
  lastUsed?: string | null;
  /** Filters only: share of rows where the expression is non-null, 0–1. */
  populatedPct?: number | null;
  distinctValues?: number | null;
  /** Metrics only: set when a required count is dead or unverifiable. */
  dataStatus?: string;
  dataStatusDetail?: string;
}

export interface GlossaryComponent {
  name: string;
  category: string;
  /** The count's WHERE condition, as SQL with constants inlined. */
  condition: string;
  description?: string;
  descriptionSource?: string;
}

interface GlossaryEntryBase {
  name: string;
  group: string;
  status: FilterStatus;
  statusReason?: string;
  description?: string;
  descriptionSource?: string;
  registeredIn: string[];
  offeredIn: string[];
  stats?: GlossaryStats;
  /** A hand-written line on what the code can't say, e.g. how two filters relate. */
  note?: string;
  hasAuthoredNote: boolean;
  /** The id of the guide that covers this entry. */
  guide?: string;
}

export interface GlossaryFilter extends GlossaryEntryBase {
  kind: "filter";
  /** Row level the filter operates at — "chance", "shot", "action:pick", … */
  grain: string;
  type: string;
  ops: string[];
  expression: string;
  /** True when the expression is a CASE or function rather than a bare column. */
  computed: boolean;
  joins: string[];
  table?: string;
  column?: string;
  uuidType?: string;
  seasonCoverage?: string;
  /** Distinct values, for enumerated filters only. Capped; see valuesTruncated. */
  values?: string[];
  /** How many further values exist beyond those listed. */
  valuesTruncated?: number;
}

export interface GlossaryMetric extends GlossaryEntryBase {
  kind: "metric";
  /** Arithmetic with count names substituted, e.g. "pct(TSAPts / TSA / 2)". */
  formula: string;
  format: string;
  counts: string[];
  components: GlossaryComponent[];
  higherIsBetter: boolean | null;
}

export type GlossaryEntry = GlossaryFilter | GlossaryMetric;

/** A topic that spans several entries, written as markdown (nba_explorer/docs/glossary/). */
export interface GlossaryGuide {
  id: string;
  title: string;
  summary: string;
  /** The metrics it covers that this source offers; each carries `guide` = this id. */
  metrics: string[];
  body: string;
}

/** Deep link to a guide on the standalone glossary page. */
export const glossaryGuidePath = (environment: string, guideId: string) =>
  `/glossary/${environment}?guide=${encodeURIComponent(guideId)}`;

export interface GlossaryResponse {
  environment: string;
  filters: GlossaryFilter[];
  metrics: GlossaryMetric[];
  /** Absent on payloads cached before guides existed. */
  guides?: GlossaryGuide[];
  groups: { filters: string[]; metrics: string[] };
  sources: { vendor: string[]; stats: boolean };
  measuredSeason?: number;
  coverage: {
    entries: number;
    filters: number;
    filtersDescribed: number;
    metrics: number;
    metricsWithFormula: number;
    authored: number;
  };
}

export const isMetric = (e: GlossaryEntry): e is GlossaryMetric =>
  e.kind === "metric";

/** Statuses that mean "resolves, but no longer offered in the query builder". */
export const HIDDEN_STATUSES: FilterStatus[] = ["deprecated", "unavailable"];

export const isHidden = (e: GlossaryEntry) => HIDDEN_STATUSES.includes(e.status);

/**
 * Whether an entry is kept out of the browsable list and surfaced only when a
 * search matches it.
 *
 * Broader than `isHidden`: `rare` entries are still offered in the query
 * builder, but 300-odd of them padding the browse list is what makes the
 * glossary hard to scan. Someone who knows the name can still find it, and it
 * arrives carrying its status chip so the reason is visible.
 */
export const isSearchOnly = (e: GlossaryEntry) => e.status !== "active";

/**
 * Human label for a grain. The raw values encode the action type after a colon
 * so they sort together; the UI wants the readable half.
 */
export function grainLabel(grain: string): string {
  if (!grain) return "";
  const [level, detail] = grain.split(":");
  if (level === "action" && detail) return `${detail} action`;
  return level;
}
